import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { environment } from '../../environments/environment';

export interface BalanceUpdate {
  organization_id: string;
  credits: number;
  money_equivalent: number;
  last_updated: number;
}

@Injectable({
  providedIn: 'root'
})
export class BalanceStreamService {
  private eventSource: EventSource | null = null;
  private balanceSubject = new BehaviorSubject<BalanceUpdate | null>(null);
  private connectionSubject = new BehaviorSubject<boolean>(false);

  // Public observables for components
  balance$ = this.balanceSubject.asObservable();
  connection$ = this.connectionSubject.asObservable();

  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10;
  private currentOrgId: string | null = null;

  constructor(private ngZone: NgZone) {}

  connect(organizationId: string): void {
    // Validate organization ID
    if (!organizationId || organizationId === 'test-org-id') {
      console.warn('Invalid organization ID, skipping SSE connection');
      this.connectionSubject.next(false);
      return;
    }

    // If already connected to the same org, do nothing
    if (this.eventSource && this.currentOrgId === organizationId) {
      return;
    }

    // Close existing connection if any
    this.disconnect();

    this.currentOrgId = organizationId;
    this.reconnectAttempts = 0;

    const url = `${this.getBalanceStreamUrl(organizationId)}`;

    try {
      this.ngZone.runOutsideAngular(() => {
        this.eventSource = new EventSource(url);

      this.eventSource.onmessage = (event) => {
        try {
          const data: BalanceUpdate = JSON.parse(event.data);
          // Run inside Angular zone so UI updates immediately
          this.ngZone.run(() => {
            this.balanceSubject.next(data);
          });
        } catch (e) {
          // console.error('Failed to parse SSE message', event.data);
        }
      };

      this.eventSource.onerror = (error) => {
        // console.error('SSE Error:', error);
        this.ngZone.run(() => {
          this.connectionSubject.next(false);
          this.attemptReconnect(organizationId);
        });
      };

      this.eventSource.onopen = () => {
        this.ngZone.run(() => {
          this.connectionSubject.next(true);
          this.reconnectAttempts = 0; // Reset on successful connection
        });
      };
      });
    } catch (error) {
      // console.error('Failed to create EventSource:', error);
      this.ngZone.run(() => {
        this.connectionSubject.next(false);
      });
    }
  }

  private attemptReconnect(organizationId: string): void {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      const delay = Math.min(3000 * this.reconnectAttempts, 30000); // Max 30 seconds
      // console.log(`Attempting to reconnect balance stream in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);

      setTimeout(() => {
        if (this.currentOrgId === organizationId) {
          this.connect(organizationId);
        }
      }, delay);
    } else {
      // console.error('Max reconnection attempts reached for balance stream');
    }
  }

  disconnect(): void {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
      this.connectionSubject.next(false);
      this.currentOrgId = null;
    }
  }

  private getBalanceStreamUrl(organizationId: string): string {
    return `${environment.balanceStreamUrl}${organizationId}/stream`;
  }

  getCurrentBalance(): BalanceUpdate | null {
    return this.balanceSubject.value;
  }

  isConnected(): boolean {
    return this.connectionSubject.value;
  }
}


// import { Injectable, NgZone } from '@angular/core';
// import { BehaviorSubject, Subscription, timer } from 'rxjs';
// import { switchMap, take } from 'rxjs/operators';
// import { HttpClient } from '@angular/common/http';
// import { environment } from '../../environments/environment';

// export interface BalanceUpdate {
//   organization_id: string;
//   credits: number;
//   money_equivalent: number;
//   last_updated: number;
// }

// @Injectable({
//   providedIn: 'root'
// })
// export class BalanceStreamService {

//   private balanceSubject = new BehaviorSubject<BalanceUpdate | null>(null);
//   private connectionSubject = new BehaviorSubject<boolean>(false);

//   balance$ = this.balanceSubject.asObservable();
//   connection$ = this.connectionSubject.asObservable();

//   private pollingSubscription: Subscription | null = null;
//   private currentOrgId: string | null = null;

//   constructor(private http: HttpClient) {}

//   connect(organizationId: string): void {

//     if (!organizationId) return;

//     this.disconnect();
//     this.currentOrgId = organizationId;

//     this.startPollingCycle(organizationId);
//   }

//   private startPollingCycle(organizationId: string) {

//     this.pollingSubscription = timer(0, 10000) // every 10 seconds
//       .pipe(
//         take(6), // only 6 times
//         switchMap(() => 
//           this.http.get<BalanceUpdate>(
//             `${environment.balanceStreamUrl}${organizationId}`
//           )
//         )
//       )
//       .subscribe({
//         next: (data) => {
//           this.balanceSubject.next(data);
//           this.connectionSubject.next(true);
//         },
//         error: () => {
//           this.connectionSubject.next(false);
//         },
//         complete: () => {
//           // ⏸ Pause for 1 minute
//           setTimeout(() => {
//             if (this.currentOrgId) {
//               this.startPollingCycle(this.currentOrgId);
//             }
//           }, 60000); // 1 minute
//         }
//       });
//   }

//   disconnect(): void {
//     if (this.pollingSubscription) {
//       this.pollingSubscription.unsubscribe();
//       this.pollingSubscription = null;
//     }
//     this.connectionSubject.next(false);
//     this.currentOrgId = null;
//   }

//   getCurrentBalance(): BalanceUpdate | null {
//     return this.balanceSubject.value;
//   }

//   isConnected(): boolean {
//     return this.connectionSubject.value;
//   }
// }
